import { LayoutContainer, LayoutText } from '@pixi/layout/components'; // eslint-disable-line
import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { APP_WIDTH, APP_HEIGHT, CARD_WIDTH, CARD_HEIGHT } from '../utils/commonUtils';

import ThreeDimensionalCardContainer from '../containers/threeDimensionalCardContainer';
import RotateableSpriteContainer from '../containers/rotateableSpriteContainer';
import Tappable from '../../common/containers/Tappable';
import TappableGraphicsButton from '../../common/containers/TappableGraphicsButton';

const formatTime = (ms) => {
    // ms: number of milliseconds
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export default class VsScreenManager {
    constructor(isFindMatchMode, roomName, width = APP_WIDTH, height = APP_HEIGHT) {
        this.scene = new PIXI.Container();
        this.scene.label = 'vsScreen';

        this.isFindMatchMode = isFindMatchMode;
        this.roomName = roomName;

        this.width = width;
        this.height = height;

        this.timer = 0;
    }

    static async loadAssets() {
        await Promise.all([
            PIXI.Assets.load([
                { src: '/assets/vs_screen/background.webp', alias: 'vsScreenBackground' },
                { src: '/assets/vs_screen/leader_backface.png', alias: 'leaderBackface' },
                { src: '/assets/vs_screen/vs_logo.png', alias: 'vsLogo' },
                { src: '/assets/vs_screen/swap_icon.png', alias: 'swapIcon' },
                { src: '/assets/vs_screen/ready_button.png', alias: 'readyButton' },
                { src: '/assets/vs_screen/ready_check.png', alias: 'readyCheck' },
                { src: '/assets/vs_screen/green_glow.png', alias: 'greenGlow' },
                { src: '/assets/vs_screen/cancel_button.png', alias: 'cancelButton' },
                { src: '/assets/vs_screen/back.png', alias: 'backButton' },
                { src: '/assets/vs_screen/compass_base.png', alias: 'compassBase' },
                { src: '/assets/vs_screen/compass_pointer.png', alias: 'compassPointer' },
                { src: '/assets/vs_screen/compass_shine.png', alias: 'compassShine' },
                { src: '/assets/vs_screen/game_id_copy.png', alias: 'gameIdCopy' },
            ]),
            ThreeDimensionalCardContainer.loadAssets(),
        ]);
    }

    attachToPixiApp(app) {
        app.stage.addChild(this.scene);
        this.startTimer();
    }

    detachFromPixiApp(app) {
        app.stage.removeChild(this.scene);
        PIXI.Ticker.shared.remove(this.compassPointerSpinTicker);
        this.stopTimer();
    }

    buildBackground() {
        const background = new PIXI.Sprite(PIXI.Assets.get('vsScreenBackground'));
        background.x = this.width / 2;
        background.y = this.height / 2;
        background.scale.set(1.03);
        background.anchor.set(0.5);
        this.scene.addChild(background);
    }
    
    buildPlayer1() {
        const player1NameContainer = new PIXI.Container();
        player1NameContainer.label = 'player1NameContainer';
        player1NameContainer.x = this.width / 2 + 110;
        player1NameContainer.y = this.height / 2 - 640;
        this.scene.addChild(player1NameContainer);

        const player1NameText = new LayoutText({
            text: this.userName,
            style: {
                fontFamily: 'Caladea',
                fontSize: 70,
                fontWeight: '600',
                fill: '#ffffff',
                dropShadow: {
                    color: '#000000',
                    blur: 0,
                    angle: Math.PI / 2,
                    distance: 4,
                },
            },
            layout: {
                width: 360,
                height: 100,
                objectPosition: 'center',
                // backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }
        });
        player1NameText.label = 'player1NameText';
        player1NameText.x = 10;
        player1NameText.y = 0;
        player1NameContainer.addChild(player1NameText);

        const player1Card = new ThreeDimensionalCardContainer({ imgUrl: `${this.selectedDeck.leader}.png`, type: 'Leader' });
        player1Card.label = 'player1Card';
        player1Card.x = this.width / 2 + 250;
        player1Card.y = this.height / 2 - 220;
        player1Card.angle = 9;
        player1Card.rotate(0, 20, 400);
        player1Card.scale.set(2.1);
        this.scene.addChild(player1Card);

        const player1ReadyGlow = new RotateableSpriteContainer({ texture: PIXI.Assets.get('greenGlow'), width: CARD_WIDTH, height: CARD_HEIGHT });
        player1ReadyGlow.label = 'player1ReadyGlow';
        player1ReadyGlow.x = this.width / 2 + 250;
        player1ReadyGlow.y = this.height / 2 - 220;
        player1ReadyGlow.angle = 9;
        player1ReadyGlow.rotate(0, 20, 400);
        player1ReadyGlow.scale.set(2.15);
        player1ReadyGlow.visible = false;
        this.scene.addChild(player1ReadyGlow);

        const player1ReadyCheck = new PIXI.Sprite(PIXI.Assets.get('readyCheck'));
        player1ReadyCheck.label = 'player1ReadyCheck';
        player1ReadyCheck.x = this.width / 2 + 190;
        player1ReadyCheck.y = this.height / 2 + 70;
        player1ReadyCheck.anchor.set(0.5);
        player1ReadyCheck.visible = false;
        this.scene.addChild(player1ReadyCheck);
    }

    buildPlayer2() {
        const player2NameContainer = new PIXI.Container();
        player2NameContainer.label = 'player2NameContainer';
        player2NameContainer.x = this.width / 2 - 377 - 110;
        player2NameContainer.y = this.height / 2 - 640;
        this.scene.addChild(player2NameContainer);
        
        const player2NameText = new LayoutText({
            text: this.opponentName || '',
            style: {
                fontFamily: 'Caladea',
                fontSize: 70,
                fontWeight: '600',
                fill: '#ffffff',
                dropShadow: {
                    color: '#000000',
                    blur: 0,
                    angle: Math.PI / 2,
                    distance: 4,
                },
            },
            layout: {
                width: 360,
                height: 100,
                objectPosition: 'center',
            }
        });
        player2NameText.label = 'player2NameText';
        player2NameText.x = 10;
        player2NameText.y = 0;
        player2NameContainer.addChild(player2NameText);

        const player2Card = new ThreeDimensionalCardContainer({ imgUrl: `${this.selectedDeck.leader}.png`, type: 'Leader' });
        player2Card.label = 'player2Card';
        player2Card.x = this.width / 2 - 250;
        player2Card.y = this.height / 2 - 220;
        player2Card.angle = -9;
        player2Card.rotate(0, -20 + 180, 400);
        player2Card.scale.set(2.1);
        this.scene.addChild(player2Card);

        const player2ReadyGlow = new RotateableSpriteContainer({ texture: PIXI.Assets.get('greenGlow'), width: CARD_WIDTH, height: CARD_HEIGHT });
        player2ReadyGlow.label = 'player2ReadyGlow';
        player2ReadyGlow.x = this.width / 2 - 250;
        player2ReadyGlow.y = this.height / 2 - 220;
        player2ReadyGlow.angle = -9;
        player2ReadyGlow.rotate(0, -20 + 180, 400);
        player2ReadyGlow.scale.set(2.15);
        player2ReadyGlow.visible = this.isOpponentReady;
        this.scene.addChild(player2ReadyGlow);

        const player2ReadyCheck = new PIXI.Sprite(PIXI.Assets.get('readyCheck'));
        player2ReadyCheck.label = 'player2ReadyCheck';
        player2ReadyCheck.x = this.width / 2 - 190;
        player2ReadyCheck.y = this.height / 2 + 70;
        player2ReadyCheck.anchor.set(0.5);
        player2ReadyCheck.visible = this.isOpponentReady;
        this.scene.addChild(player2ReadyCheck);
    }

    buildPlayers() {
        this.buildPlayer1();
        this.buildPlayer2();
    }

    buildVsLogo() {
        const vsLogo = new PIXI.Sprite(PIXI.Assets.get('vsLogo'));
        vsLogo.x = this.width / 2;
        vsLogo.y = this.height / 2 - 220;
        vsLogo.anchor.set(0.5);
        this.scene.addChild(vsLogo);
    }
    
    buildSelectDeckButton(selectedDeck) {
        // const selectDeckText = new PIXI.Text({
        //     text: 'SELECT DECK',
        //     style: {
        //         fontFamily: 'Caladea',
        //         fontSize: 68,
        //         fontWeight: '700',
        //         letterSpacing: 6,
        //         fill: '#f9edb5',
        //         stroke: { color: '#275363', width: 8 },
        //     },
        // });
        // selectDeckText.x = this.width / 2;
        // selectDeckText.y = this.height / 2 + 430;
        // selectDeckText.anchor.set(0.5);
        // this.scene.addChild(selectDeckText);

        const buttonWidth = 700;
        const buttonHeight = 110;

        this.deckButtonContainer = new Tappable();
        this.deckButtonContainer.label = 'deckButtonContainer';
        this.deckButtonContainer.x = this.width / 2 - buttonWidth / 2;
        this.deckButtonContainer.y = this.height / 2 + 550 - buttonHeight / 2;
        this.deckButtonContainer.hitArea = new PIXI.Rectangle(0, 0, buttonWidth, buttonHeight);
        this.scene.addChild(this.deckButtonContainer);

        this.deckButtonContainer.onTap = () => {
            this.onSelectDeckButtonClicked?.();
        };

        const scaleContainer = new PIXI.Container();
        scaleContainer.x = buttonWidth / 2;
        scaleContainer.y = buttonHeight / 2;
        this.deckButtonContainer.addChild(scaleContainer);

        this.deckButtonContainer.onHoldStart = () => {
            scaleContainer.scale.set(0.9);
        };
        this.deckButtonContainer.onHoldEnd = () => {
            scaleContainer.scale.set(1);
        };

        const content = new PIXI.Container();
        content.x = -buttonWidth / 2;
        content.y = -buttonHeight / 2;
        scaleContainer.addChild(content);

        const deckNameBackground = new PIXI.Graphics();
        deckNameBackground.label = 'deckNameBackground';
        deckNameBackground.roundRect(0, 0, buttonWidth, buttonHeight, buttonHeight / 2);
        deckNameBackground.fill({ color: 'rgba(0, 0, 0, 0.7)' });
        deckNameBackground.stroke({ color: 'rgba(255, 255, 255, 0.2)', width: 5 });
        content.addChild(deckNameBackground);

        const swapIcon = new PIXI.Sprite(PIXI.Assets.get('swapIcon'));
        swapIcon.label = 'swapIcon';
        swapIcon.width = 68;
        swapIcon.height = 75;
        swapIcon.x = 35;
        swapIcon.y = (buttonHeight) / 2;
        swapIcon.anchor.set(0, 0.5);
        content.addChild(swapIcon);

        this.deckNameText = new LayoutText({
            text: '',
            style: {
                fontFamily: 'Caladea',
                fontSize: 50,
                fontWeight: '600',
                fill: '#ffffff',
            },
            layout: {
                width: buttonWidth - 240,
                height: buttonHeight,
                objectPosition: 'center',
                objectFit: 'none',
                overflow: 'hidden',
            },
        });
        this.deckNameText.label = 'deckNameText';
        this.deckNameText.x = 125;
        this.deckNameText.y = 0;
        this.deckNameMaxWidth = buttonWidth - 240;
        this.setDeckNameText(selectedDeck.name);

        content.addChild(this.deckNameText);
    }

    setDeckNameText(name) {
        if (!this.deckNameText) return;

        let displayedName = name;
        this.deckNameText.slot.text = displayedName;

        while (this.deckNameText.slot.width > this.deckNameMaxWidth && displayedName.length > 0) {
            displayedName = displayedName.slice(0, -1);
            this.deckNameText.slot.text = displayedName + '…';
        }
    }

    buildTimer() {
        this.timerContainer = new LayoutContainer();
        this.timerContainer.label = 'timerContainer';
        this.timerContainer.layout = {
            width: this.width,
            height: 115,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
        };
        this.timerContainer.x = 0;
        this.timerContainer.y = this.height / 2 + 340;
        this.scene.addChild(this.timerContainer);

        const colorGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0.5, y: 0 },
            end: { x: 0.5, y: 1 },
            colorStops: [
                { offset: 0, color: '#F1CC44' },
                { offset: 1, color: '#f9f2d8' },
            ],
        });

        this.timerText = new LayoutText({
            text: '00:00',
            style: {
                fontFamily: 'Fugaz One',
                fontSize: 115,
                fontWeight: '400',
                fill: colorGradient,
                stroke: { color: '#553917', width: 8 },
                dropShadow: {
                    color: '#000000',
                    blur: 0,
                    alpha: 0.35,
                    angle: Math.PI / 2,
                    distance: 10,
                },  
            },
        });
        this.timerText.label = 'timerText';
        this.timerText.layout = {
            width: 500,
            height: 200,
            objectPosition: 'center',
        };
        this.timerText.x = 0;
        this.timerText.y = 0;
        this.timerContainer.addChild(this.timerText);
    }

    buildWaitingForOpponent() {
        this.waitingForOpponentContainer = new LayoutContainer();
        this.waitingForOpponentContainer.label = 'waitingForOpponentContainer';
        this.waitingForOpponentContainer.layout = {
            width: this.width,
            height: 200,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 30,
        };
        this.waitingForOpponentContainer.x = 0;
        this.waitingForOpponentContainer.y = this.height / 2 + 430;
        this.scene.addChild(this.waitingForOpponentContainer);

        const radialGradient = new PIXI.FillGradient({
            type: 'radial',
            start: { x: 0.5, y: 0.5 },
            end: { x: 1, y: 1 },
            colorStops: [
                { offset: 0, color: '#000000' },
                { offset: 1, color: 'rgba(0, 0, 0, 0)' },
            ],
        });

        const bgSize = 200;
        const bgGraphics = new PIXI.Graphics();
        bgGraphics.label = 'bgGraphics';
        bgGraphics.rect(-bgSize / 2, -bgSize / 2, bgSize, bgSize);
        bgGraphics.fill(radialGradient);
        bgGraphics.x = this.width / 2;
        bgGraphics.y = 100;
        bgGraphics.scale.set(6, 1);
        this.waitingForOpponentContainer.addChild(bgGraphics);

        const compassContainer = new LayoutContainer();
        compassContainer.label = 'compassContainer';
        compassContainer.layout = {
            width: 111,
            height: 111,
        };
        this.waitingForOpponentContainer.addChild(compassContainer);

        const compassBase = new PIXI.Sprite(PIXI.Assets.get('compassBase'));
        compassBase.label = 'compassBase';
        compassContainer.addChild(compassBase);

        const compassPointer = new PIXI.Sprite(PIXI.Assets.get('compassPointer'));
        compassPointer.label = 'compassPointer';
        compassPointer.x = 111 / 2;
        compassPointer.y = 111 / 2;
        compassPointer.anchor.set(0.5);
        compassContainer.addChild(compassPointer);

        this.compassPointerSpinTicker = ticker => {
            compassPointer.angle += (ticker.deltaMS / 1000) * 180;
        };
        PIXI.Ticker.shared.add(this.compassPointerSpinTicker);

        const compassShine = new PIXI.Sprite(PIXI.Assets.get('compassShine'));
        compassShine.label = 'compassShine';
        compassContainer.addChild(compassShine);

        this.waitingForOpponentText = new LayoutText({
            text: 'WAITING FOR OPPONENT...',
            style: {
                fontFamily: 'Caladea',
                fontSize: 50,
                fontWeight: '600',
                fill: '#ffffff',
                dropShadow: {
                    color: '#000000',
                    blur: 0,
                    angle: Math.PI / 2,
                    distance: 3,
                },
            },
        });
        this.waitingForOpponentText.layout = {
            width: 600,
            height: 200,
            objectPosition: 'center',
        };
        this.waitingForOpponentText.label = 'waitingForOpponentText';
        this.waitingForOpponentContainer.addChild(this.waitingForOpponentText);
    }

    buildActionButtons() {
        const scaleFactor = 1.25;
        const buttonHeight = 165 * scaleFactor;
        const buttonWidth = 637 * scaleFactor;
        const buttonFontSize = 63 * scaleFactor;

        this.readyButton = new TappableGraphicsButton({
            text: 'READY',
            width: buttonWidth,
            height: buttonHeight,
            backgroundColor: 'rgb(206, 131, 0, 0.7)',
            borderColor: '#c39919',
            cornerRadius: 35,
            fontSize: buttonFontSize,
            fontFamily: 'Caladea',
            fontStrokeColor: '#4c3c0b',
            fontStrokeWidth: 7,
        });
        this.readyButton.label = 'readyButton';
        this.readyButton.x = this.width / 2 - buttonWidth / 2;
        this.readyButton.y = this.height / 2 + 750 - buttonHeight / 2;
        this.scene.addChild(this.readyButton);

        this.readyButton.onButtonTap = () => {
            this.onReadyButtonClicked?.();
        };
   


        this.cancelButton = new TappableGraphicsButton({
            text: 'CANCEL',
            width: buttonWidth,
            height: buttonHeight,
            backgroundColor: 'rgba(124, 30, 27, 0.7)',
            borderColor: '#8a4140',
            cornerRadius: 35,
            fontSize: buttonFontSize,
            fontFamily: 'Caladea',
            fontStrokeColor: '#420b00',
            fontStrokeWidth: 7,
        });
        this.cancelButton.label = 'cancelButton';
        this.cancelButton.x = this.width / 2 - buttonWidth / 2;
        this.cancelButton.y = this.height / 2 + 750 - buttonHeight / 2;
        this.cancelButton.visible = false;
        this.scene.addChild(this.cancelButton);

        this.cancelButton.onButtonTap = () => {
            this.onCancelButtonClicked?.();
        };
    }

    buildBackButton() {
        const backButton = new PIXI.Sprite(PIXI.Assets.get('backButton'));
        backButton.label = 'backButton';
        backButton.x = 130;
        backButton.y = 290;
        backButton.anchor.set(0.5);
        backButton.eventMode = 'static';
        this.scene.addChild(backButton);

        backButton.on('pointerdown', () => {
            this.onBackButtonClicked?.();
        });
    }

    buildCopyRoomNameButton() {
        // Create a layout container to hold text and icon
        const container = new LayoutContainer({
            x: 0,
            y: 2150,
            layout: {
                direction: 'row',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: this.width,
                height: 100,
                paddingRight: 80,
                gap: 18,
            },
        });
        container.label = 'gameIdCopyContainer';

        // Game ID Text
        const gameIdText = new LayoutText({
            text: `Game ID : ${this.roomName}`,
            style: {
                fontFamily: 'Open Sans',
                fontSize: 55,
                fontWeight: '600',
                fill: '#ffffff',
            },
            layout: {
                width: 350,
                height: 60,
       
            },
        });
        gameIdText.label = 'gameIdText';

        // Copy Icon
        const iconTexture = PIXI.Assets.get('gameIdCopy');
        const icon = new PIXI.Sprite(iconTexture);
        icon.label = 'gameIdCopyIcon';
        icon.eventMode = 'static';
        icon.layout = {
            width: 29,
            height: 32,
        };

        // Add to container
        container.addChild(gameIdText);
        container.addChild(icon);

        this.scene.addChild(container);

        // Shared click handler for text and icon
        const onCopy = () => {
            this.onCopyRoomNameButtonClicked?.();
        };
        gameIdText.eventMode = 'static';
        gameIdText.on('pointerdown', onCopy);
        icon.on('pointerdown', onCopy);
    }

    buildVsScreen(userName, selectedDeck, opponentName, isOpponentReady) {
        this.userName = userName;
        this.selectedDeck = selectedDeck;
        this.opponentName = opponentName;
        this.isOpponentReady = isOpponentReady;

        this.buildBackground();
        this.buildPlayers();
        this.buildVsLogo();

        if (!this.isFindMatchMode) {
            this.buildSelectDeckButton(selectedDeck);
        } else {
            this.buildTimer();
            this.buildWaitingForOpponent();
        }

        this.buildActionButtons();

        if (!this.isFindMatchMode) {
            this.buildBackButton();
            this.buildCopyRoomNameButton();
        }

        const blackFadeOverlayGraphics = new PIXI.Graphics();
        blackFadeOverlayGraphics.label = 'blackFadeOverlayGraphics';
        blackFadeOverlayGraphics.rect(0, 0, this.width, this.height);
        blackFadeOverlayGraphics.fill({ color: '#000000' });
        blackFadeOverlayGraphics.alpha = 0;
        blackFadeOverlayGraphics.visible = false;
        blackFadeOverlayGraphics.eventMode = 'static';
        this.scene.addChild(blackFadeOverlayGraphics);
    }

    resize(width, height) {
        this.width = width;
        this.height = height;
        this.scene.removeChildren();
        PIXI.Ticker.shared.remove(this.compassPointerSpinTicker);
        this.buildVsScreen(this.userName, this.selectedDeck, this.opponentName, this.isOpponentReady);
    }

    destroy() {
        PIXI.Ticker.shared.remove(this.rotationTicker);
        super.destroy();
    }

    changeDeck(deck) {
        console.log('changeDeck', deck);

        this.selectedDeck = deck;
        this.setDeckNameText(deck.name);

        const player1Card = this.scene.getChildByLabel('player1Card');
        player1Card.changeCard({ imgUrl: `${deck.leader}.png`, type: 'Leader' });
    }

    updateRoomState(userId, roomState) {
        console.log('updateRoomState', userId, roomState);

        const user = roomState.users.find(user => user.userId === userId);
        const opponent = roomState.users.find(user => user.userId !== userId);

        const player1ReadyCheck = this.scene.getChildByLabel('player1ReadyCheck', true);
        const player1ReadyGlow = this.scene.getChildByLabel('player1ReadyGlow', true);
        const readyButton = this.scene.getChildByLabel('readyButton');
        const cancelButton = this.scene.getChildByLabel('cancelButton');
        const deckButtonContainer = this.deckButtonContainer;
        player1ReadyCheck.visible = false;
        player1ReadyGlow.visible = false;
        readyButton.visible = false;
        cancelButton.visible = false;

        if (!this.isFindMatchMode) {
            deckButtonContainer.eventMode = 'static';
        }

        if (user) {
            player1ReadyCheck.visible = user.readyToPlay;
            player1ReadyGlow.visible = user.readyToPlay;
            readyButton.visible = !user.readyToPlay;
            cancelButton.visible = user.readyToPlay;

            if (!this.isFindMatchMode) {
                deckButtonContainer.eventMode = !user.readyToPlay ? 'static' : 'none';
            }
        }

        const player2NameText = this.scene.getChildByLabel('player2NameText', true);
        const player2ReadyCheck = this.scene.getChildByLabel('player2ReadyCheck', true);
        const player2ReadyGlow = this.scene.getChildByLabel('player2ReadyGlow', true);

        player2NameText.slot.text = '';
        player2ReadyCheck.visible = false;
        player2ReadyGlow.visible = false;

        if (opponent) {
            player2NameText.slot.text = opponent.username;
            player2ReadyCheck.visible = opponent.readyToPlay;
            player2ReadyGlow.visible = opponent.readyToPlay;

            if (this.isFindMatchMode) {
                this.waitingForOpponentContainer.visible = false;
                this.timerContainer.visible = false;
                this.cancelButton.visible = false;
            }
        }
    }

    async showOpponentLeaderAnimation(opponentLeader) {
        console.log('showOpponentLeaderAnimation', opponentLeader);

        const player2Card = this.scene.getChildByLabel('player2Card', true);
        const player2ReadyGlow = this.scene.getChildByLabel('player2ReadyGlow', true);
        player2Card.changeCard(opponentLeader);

        let dummy = { angleY: 0 };
        await gsap.timeline({
            onUpdate: () => {
                player2Card.rotate(0, -20 + 180 + dummy.angleY, 400);
                player2ReadyGlow.rotate(0, -20 + 180 + dummy.angleY, 400);
            },
        }).to(dummy, {
            duration: 1,
            angleY: 180,
            ease: 'power1.inOut',
        });

        const blackFadeOverlayGraphics = this.scene.getChildByLabel('blackFadeOverlayGraphics');
        blackFadeOverlayGraphics.visible = true;

        await gsap.timeline().call(() => {}, [], '+=1').to(blackFadeOverlayGraphics, {
            duration: 1,
            pixi: {
                alpha: 1,
            },
        });
    }

    startTimer() {
        this.stopTimer();

        this._timerStartedAt = Date.now();
        this.timer = 0;
        if (this.timerText) {
            this.timerText.slot.text = formatTime(this.timer);
        }

        this._timerTick = () => {
            this.timer = Date.now() - this._timerStartedAt;
            if (this.timerText) {
                this.timerText.slot.text = formatTime(this.timer);
            }
        };
        this._timerInterval = setInterval(this._timerTick, 1000);
    }

    stopTimer() {
        if (this._timerInterval) {
            clearInterval(this._timerInterval);
            this._timerInterval = null;
        }
        this._timerTick = null;
        this._timerStartedAt = null;
        this.timer = 0;
        if (this.timerText) {
            this.timerText.slot.text = formatTime(this.timer);
        }
    }
}